// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Primi passi con LabThree"
// (Centro assistenza → prima guida)
//
// Layout:
//  - Header con titolo, sottotitolo, durata, audience
//  - TOC sticky a sinistra (scroll-spy semplice)
//  - Articolo con sezioni (CatTile colorati), callout, mockup placeholder,
//    FAQ accordion, footer feedback
//
// Mockup degli screenshot: <MockFrame imageSrc="/screenshots/xxx.png" /> con
// marker in coordinate percentuali (così se cambia la risoluzione dell'img i
// marker restano nel posto giusto). Gli screenshot stanno in /public/screenshots/.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import { Chip, CatTile } from '../../ui/components';
// Atomi di scrittura condivisi con le altre guide. Erano definiti qui sotto in
// copia locale: due MockFrame diversi avrebbero finito per divergere.
import {
  Strong, Code, P, H, Callout, MockFrame, FAQ,
} from './atomi';

// ─────── Sezioni della guida (per TOC) ───────────────────────────────────────

const SECTIONS = [
  { id: 's0', label: 'Introduzione' },
  { id: 's1', label: '1. Crea un operatore', cat: 'formazione' },
  { id: 's2', label: '2. Definisci gli oggetti', cat: 'temperature' },
  { id: 's3', label: '3. Pianifica le attività', cat: 'manutenzioni' },
  { id: 's4', label: '4. Esegui i monitoraggi', cat: 'produzione' },
  { id: 's5', label: 'Domande frequenti' },
  { id: 's6', label: 'E adesso?' },
];

// ─────── Componente principale ───────────────────────────────────────────────

export default function PrimiPassi() {
  const [active, setActive] = useState('s0');
  const articleRef = useRef(null);

  // Scroll-spy: aggiorna `active` in base alla sezione visibile
  useEffect(() => {
    const sections = SECTIONS.map(s => document.getElementById(s.id)).filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.01 }
    );
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const goTo = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{
      maxWidth: 1180, margin: '0 auto', padding: '0 24px 64px',
      fontFamily: LT.sans, color: LT.n800,
    }}>
      {/* Header */}
      <div style={{ padding: '32px 0 28px' }}>
        <h1 style={{
          fontSize: 38, fontWeight: 700, color: LT.n900,
          letterSpacing: '-0.02em', margin: '0 0 12px', lineHeight: 1.15,
        }}>
          Primi passi con LabThree
        </h1>
        <p style={{ fontSize: 16, color: LT.n500, lineHeight: 1.6, maxWidth: 760, margin: 0 }}>
          Guida pratica al setup iniziale del tuo workspace HACCP. In meno di dieci minuti
          avrai un sistema di registrazioni e tracciabilità funzionante e popolato con dati
          realistici.
        </p>
        <div style={{
          display: 'flex', gap: 14, marginTop: 20, alignItems: 'center',
          fontSize: 13, color: LT.n500, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Ico d={I.clock} size={14} />~ 10 minuti
          </span>
          <span style={{ color: LT.n300 }}>·</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Ico d={I.user} size={14} />Per amministratori
          </span>
          <span style={{ color: LT.n300 }}>·</span>
          <Chip kind="blu" size="sm">Aggiornata oggi</Chip>
        </div>
      </div>

      {/* Body grid */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24 }}>
        {/* TOC sticky */}
        <aside style={{
          width: 240, flexShrink: 0,
          position: 'sticky', top: 70, alignSelf: 'flex-start',
          padding: '8px 0',
        }} className="primi-passi-toc">
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12,
            paddingLeft: 12,
          }}>
            In questa pagina
          </div>
          {SECTIONS.map(s => (
            <a
              key={s.id} href={`#${s.id}`}
              onClick={e => { e.preventDefault(); goTo(s.id); }}
              style={{
                display: 'block', padding: '7px 12px', borderRadius: LT.r.sm,
                fontSize: 13, color: active === s.id ? LT.blu : LT.n600,
                background: active === s.id ? LT.bluSoft : 'transparent',
                fontWeight: active === s.id ? 600 : 500,
                textDecoration: 'none', marginBottom: 2,
                borderLeft: active === s.id ? `2px solid ${LT.blu}` : '2px solid transparent',
              }}
            >
              {s.label}
            </a>
          ))}

          {/* CTA "hai bisogno di aiuto?" */}
          <div style={{
            marginTop: 24, padding: 16,
            background: '#fff', borderRadius: LT.r.md, border: `1px solid ${LT.n150}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: LT.n800, marginBottom: 6 }}>
              Hai bisogno di aiuto?
            </div>
            <div style={{ fontSize: 12, color: LT.n500, lineHeight: 1.5, marginBottom: 10 }}>
              Scrivici e un consulente HACCP ti contatterà entro la giornata lavorativa.
            </div>
            <a
              href="mailto:info@labthree.it?subject=Richiesta%20supporto%20—%20Primi%20passi"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 12px', background: LT.bluSoft, color: LT.blu,
                borderRadius: LT.r.sm, fontSize: 12, fontWeight: 600,
                textDecoration: 'none', border: `1px solid ${LT.blu}33`,
              }}
            >
              <Ico d={I.mail} size={13} stroke={LT.blu} />
              info@labthree.it
            </a>
          </div>
        </aside>

        {/* Articolo */}
        <article ref={articleRef} style={{
          flex: 1, minWidth: 0, maxWidth: 800, padding: '8px 0 64px',
          background: '#fff', borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
          paddingLeft: 36, paddingRight: 36,
        }}>
          {/* Sezione 0 — Introduzione */}
          <section id="s0" style={{ scrollMarginTop: 80, paddingTop: 28 }}>
            <P style={{ marginTop: 0 }}>
              <Strong>LabThree</Strong> è il tuo registro HACCP digitale: gestisce le
              registrazioni di tracciabilità, i monitoraggi periodici e le non
              conformità. Per partire bene segui questi quattro passi nell'ordine —
              sono pensati per costruire un setup di prova realistico, da cui poi
              estendere a tutto l'impianto HACCP.
            </P>

            {/* Sommario "Cosa imparerai" */}
            <div style={{
              padding: '20px 22px', background: LT.gradSoft || 'linear-gradient(135deg, rgba(37,99,235,.08) 0%, rgba(34,197,94,.08) 100%)',
              borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
              margin: '20px 0',
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: LT.blu,
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14,
              }}>
                Cosa imparerai
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { d: I.users,    t: 'Creare un operatore con ruoli e PIN' },
                  { d: I.package,  t: 'Modellare ciò che monitori (oggetti)' },
                  { d: I.calendar, t: 'Pianificare attività ricorrenti' },
                  { d: I.play,     t: 'Registrare i primi monitoraggi' },
                ].map((it, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: 13.5, color: LT.n700,
                  }}>
                    <Ico d={it.d} size={16} stroke={LT.blu} />
                    <span>{it.t}</span>
                  </div>
                ))}
              </div>
            </div>

            <Callout kind="info" titolo="Cosa avrai alla fine">
              Un operatore configurato, tre oggetti rappresentativi (superficie, cella
              frigo, addetto), tre attività ricorrenti pianificate e i primi
              monitoraggi nello storico.
            </Callout>
          </section>

          {/* Sezione 1 — Operatore */}
          <H id="s1" cat="formazione">1. Crea il primo operatore</H>
          <P>
            Gli operatori sono le persone che useranno LabThree per eseguire i
            monitoraggi. Ogni operatore accede con un PIN di 4–6 cifre e vede solo le
            attività di propria competenza.
          </P>
          <P>
            Vai in <Strong>Gestione → Organico</Strong>, premi <Code>Nuovo operatore</Code> e
            compila i dati anagrafici. Per il setup di prova ti basta un operatore "in
            cucina" oltre al tuo profilo admin.
          </P>
          <MockFrame
            url="app.labthree.it/organico"
            imageSrc="/screenshots/organico-lista.png"
            imageAlt="Lista degli operatori in Organico"
            markers={[
              { n: 1, top: '17%', left: '88%', titolo: 'Nuovo operatore', testo: 'Bottone in alto a destra: apre la pagina di creazione con nome, cognome, PIN e permessi.' },
              { n: 2, top: '28%', left: '50%', titolo: 'Ricerca rapida', testo: 'Cerca un operatore per nome o cognome quando l\'organico cresce.' },
              { n: 3, top: '47%', left: '37%', titolo: 'Card operatore',  testo: 'Mostra ruolo (Admin/Operatore), PIN mascherato e stato della firma. Click per modificare.' },
            ]}
          />
          <P>
            Il dialog di creazione chiede solo i dati anagrafici, un PIN sicuro
            e i permessi: ti bastano pochi secondi per ogni nuovo operatore.
          </P>
          <MockFrame
            url="app.labthree.it/organico/nuovo"
            imageSrc="/screenshots/organico-nuovo.png"
            imageAlt="Form di creazione di un nuovo operatore"
            markers={[
              { n: 1, top: '34%', left: '50%', titolo: 'Nome e cognome', testo: 'Compaiono ovunque l\'operatore esegua o verifichi un\'attività.' },
              { n: 2, top: '47%', left: '50%', titolo: 'PIN di accesso', testo: 'Almeno 5 caratteri, una lettera e un numero. È personale e non condivisibile.' },
              { n: 3, top: '79%', left: '37%', titolo: 'Permessi', testo: 'Spunta "Amministratore" solo per chi deve accedere alla Gestione (organico, oggetti, scadenziario).' },
            ]}
          />
          {/* Sezione 2 — Oggetti */}
          <H id="s2" cat="temperature">2. Definisci 3 oggetti rappresentativi</H>
          <P>
            In LabThree un <Strong>oggetto</Strong> è qualsiasi entità che richiede un monitoraggio:
            una superficie, un'attrezzatura, una persona. Per partire crea uno per ogni
            macro-tipologia HACCP — tipicamente tre.
          </P>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12, margin: '16px 0',
          }}>
            {[
              { cat: 'pulizie',     t: 'Una superficie',  d: '"Banco lavorazione zona A" — apre la strada ai controlli pre-operativi giornalieri.' },
              { cat: 'temperature', t: 'Una cella frigo', d: '"Cella frigo carni +4°C" — userai i controlli temperatura con range di conformità.' },
              { cat: 'formazione',  t: 'Un addetto',      d: '"Laura Bianchi" — userai le scadenze formative e le visite mediche.' },
            ].map((o, i) => (
              <div key={i} style={{
                padding: 14, borderRadius: LT.r.md, border: `1px solid ${LT.n150}`,
                background: LT.n50,
              }}>
                <CatTile cat={o.cat} size={32} />
                <div style={{ fontSize: 13.5, fontWeight: 700, color: LT.n900, marginTop: 10 }}>{o.t}</div>
                <div style={{ fontSize: 12.5, color: LT.n500, marginTop: 4, lineHeight: 1.5 }}>{o.d}</div>
              </div>
            ))}
          </div>
          <MockFrame
            url="app.labthree.it/oggetti"
            imageSrc="/screenshots/oggetti-lista.png"
            imageAlt="Lista degli oggetti da monitorare"
            markers={[
              { n: 1, top: '17%', left: '88%', titolo: 'Nuovo oggetto', testo: 'Apre il form di creazione: descrizione, collocazione, eventuale strumento di riferimento.' },
              { n: 2, top: '30%', left: '45%', titolo: 'Tab di stato', testo: 'Filtra fra Attivi, Obsoleti e Tutti — utile quando dismetti un\'attrezzatura senza perdere lo storico.' },
              { n: 3, top: '52%', left: '37%', titolo: 'Card oggetto', testo: 'Descrizione + collocazione/mansione. Click per aprire il dettaglio e collegare lo scadenziario.' },
            ]}
          />
          <P>
            Quando crei un nuovo oggetto inserisci una descrizione chiara e la
            sua collocazione. Lo <Strong>strumento di riferimento</Strong> è
            facoltativo — serve solo se l'oggetto è collegato a un'attrezzatura
            di misura (es. un termometro identificato).
          </P>
          <MockFrame
            url="app.labthree.it/oggetti/nuovo"
            imageSrc="/screenshots/oggetti-nuovo.png"
            imageAlt="Form di creazione di un nuovo oggetto"
            markers={[
              { n: 1, top: '40%', left: '50%', titolo: 'Descrizione', testo: 'Il nome con cui l\'oggetto comparirà ovunque (lista, scadenze, storico).' },
              { n: 2, top: '54%', left: '50%', titolo: 'Collocazione', testo: 'Dove si trova fisicamente o a chi è associato (Zona A, area lavorazione, addetto…).' },
              { n: 3, top: '79%', left: '50%', titolo: 'Strumento di riferimento', testo: 'Opzionale — utile per le tarature: identifica univocamente lo strumento associato.' },
            ]}
          />

          {/* Sezione 3 — Scadenziario */}
          <H id="s3" cat="manutenzioni">3. Pianifica nello Scadenziario</H>
          <P>
            Lo <Strong>Scadenziario</Strong> è il cuore del sistema: collega ogni oggetto al tipo di
            attività, alla frequenza e a chi se ne occupa. È qui che decidi <em>cosa</em> verrà
            controllato, <em>ogni quanto</em> e da <em>chi</em>.
          </P>
          <P>Per il setup di prova crea queste voci tipiche:</P>
          <ol style={{ paddingLeft: 22, color: LT.n700, fontSize: 14.5, lineHeight: 1.85, margin: '8px 0 16px' }}>
            <li><Strong>Pre-operativa</Strong> sul Banco lavorazione (Zona A), frequenza giornaliera, monitoratore Verdi Giovanni.</li>
            <li><Strong>Controllo temperatura</Strong> sulla Cella frigo carni +4°C, giornaliero, range <Code>0 → +4 °C</Code>. Valori fuori range generano automaticamente una NC.</li>
            <li><Strong>Scadenza formazione</Strong> sull'addetto Laura Bianchi, frequenza 365 giorni.</li>
          </ol>
          <MockFrame
            url="app.labthree.it/scadenziario"
            imageSrc="/screenshots/scadenziario-lista.png"
            imageAlt="Lista delle attività programmate nello Scadenziario"
            markers={[
              { n: 1, top: '13%', left: '88%', titolo: 'Nuova attività', testo: 'Apre il form per pianificare una nuova voce di scadenziario.' },
              { n: 2, top: '21%', left: '40%', titolo: 'Tab di stato', testo: 'Filtra fra Attive, Obsolete e Tutte — utile quando una voce non è più rilevante ma vuoi mantenere lo storico.' },
              { n: 3, top: '88%', left: '47%', titolo: 'Range di conformità', testo: 'Visibile in elenco quando la voce ha valori min/max definiti (es. temperature). Fuori range si apre una NC automatica.' },
            ]}
          />
          <P>
            Il form di creazione è suddiviso in tre blocchi: <Strong>oggetto e
            tipo attività</Strong>, <Strong>frequenza e limiti</Strong>,
            <Strong> responsabili</Strong>.
          </P>
          <MockFrame
            url="app.labthree.it/scadenziario/nuova"
            imageSrc="/screenshots/scadenziario-nuovo-1.png"
            imageAlt="Form nuova attività programmata — primo step"
            markers={[
              { n: 1, top: '33%', left: '50%', titolo: 'Oggetto da monitorare', testo: 'Scegli uno degli oggetti che hai creato al passo 2.' },
              { n: 2, top: '41%', left: '50%', titolo: 'Tipo attività', testo: 'L\'ambito (Pulizia, Taratura, Formazione, ecc.) e il tipo specifico — determinano la categoria HACCP della voce.' },
            ]}
          />
          <MockFrame
            url="app.labthree.it/scadenziario/nuova"
            imageSrc="/screenshots/scadenziario-nuovo-2.png"
            imageAlt="Form nuova attività — frequenza, limiti, responsabili"
            markers={[
              { n: 1, top: '17%', left: '50%', titolo: 'Frequenza', testo: 'Numero di giorni fra una scadenza e la successiva. Lascia vuoto se l\'attività è solo a esecuzione manuale.' },
              { n: 2, top: '29%', left: '60%', titolo: 'Salta sabato/domenica', testo: 'Aspettativa per le attività giornaliere: nei giorni saltati la voce non compare fra quelle da eseguire.' },
              { n: 3, top: '40%', left: '50%', titolo: 'Valore minimo / massimo', testo: 'Range di conformità per le attività con misurazione (es. temperatura). Fuori range = NC automatica.' },
              { n: 4, top: '60%', left: '50%', titolo: 'Responsabili', testo: 'Monitoraggio (chi esegue), Supplente (chi sostituisce in assenza) e Verifica (chi controlla in doppia firma).' },
            ]}
          />
          {/* Sezione 4 — Esecuzione */}
          <H id="s4" cat="produzione">4. Esegui i primi monitoraggi</H>
          <P>
            Le voci dello scadenziario diventano <Strong>attività operative</Strong> alla data di
            scadenza. Per provarle, fai logout e accedi con il PIN dell'operatore Verdi
            Giovanni: vedrai la home con le attività di oggi raggruppate per categoria.
          </P>
          <P>
            Per ogni voce premi <Code>Conforme</Code> per registrare l'esito positivo
            o <Code>Non conforme</Code> per aprire una NC. Per le attività con range
            di temperatura inserisci il valore misurato — fuori soglia la NC viene
            aperta in automatico.
          </P>
          <MockFrame
            url="app.labthree.it/attivita"
            imageSrc="/screenshots/attivita-lista.png"
            imageAlt="Home dell'operatore con le attività da eseguire"
            markers={[
              { n: 1, top: '17%', left: '88%', titolo: 'Avvia sessione', testo: 'Apre una sessione di registrazione "datata": permette di indicare data e ora diverse da quelle correnti.' },
              { n: 2, top: '24%', left: '40%', titolo: 'Filtri di categoria', testo: 'Mostra solo Animali infestanti, Formazione, Taratura, Pulizia… per concentrarsi su un\'area alla volta.' },
              { n: 3, top: '57%', left: '37%', titolo: 'Conforme', testo: 'Registra l\'esito positivo: l\'attività finisce nello Storico e ricalcola la prossima scadenza.' },
              { n: 4, top: '57%', left: '78%', titolo: 'Non conforme', testo: 'Apre una scheda di Non Conformità da completare con causa, trattamento e azione correttiva.' },
            ]}
          />
          <Callout kind="tip" titolo="Avvia sessione: registrare nel passato">
            Di default LabThree registra ogni attività con data e ora del momento in cui premi <Code>Conforme</Code>.
            Se ti sei dimenticato di registrare un controllo l'altroieri, oppure devi trasferire un registro
            cartaceo storico, premi <Strong>Avvia sessione</Strong> in alto a destra: imposti una volta data
            e ora del momento da rappresentare e tutte le attività che registri durante la sessione vengono
            "datate" a quel momento. Quando hai finito chiudi la sessione e torni alla registrazione "in tempo reale".
          </Callout>
          <Callout kind="success" titolo="Hai chiuso il primo ciclo HACCP">
            Le attività eseguite passano nello <Strong>Storico</Strong> e — se hai assegnato un
            verificatore — diventano disponibili nella sua sezione <Strong>Verifiche</Strong> per la
            doppia firma.
          </Callout>

          {/* Sezione 5 — FAQ */}
          <H id="s5">Domande frequenti</H>
          <div style={{ marginTop: 8 }}>
            <FAQ q="Posso modificare un'attività dello scadenziario dopo averla creata?">
              Sì. Frequenza, range, monitoratore e verificatore sono tutti modificabili. Le modifiche si applicano alle <Strong>scadenze future</Strong>: lo storico delle esecuzioni passate non viene riscritto.
            </FAQ>
            <FAQ q="Cosa succede se un operatore non esegue un'attività in tempo?">
              La voce resta visibile come "in scadenza" e si accumula nei giorni successivi. L'admin la vede in dashboard tra gli alert. Per le giornaliere puoi attivare <Code>salta sabato</Code> e <Code>salta domenica</Code> per evitare scadenze nei weekend.
            </FAQ>
            <FAQ q="Come funzionano le non conformità automatiche?">
              Quando un valore registrato esce dal range definito, LabThree apre automaticamente una NC nel <Strong>Registro NC</Strong>, con riferimento all'attività di origine. L'admin la completa con causa, trattamento e azione correttiva.
            </FAQ>
            <FAQ q="Devo creare un oggetto per ogni dipendente?">
              Solo per chi ha scadenze tracciabili (formazione HACCP, visite mediche, attestati). Per chi esegue solo monitoraggi basta crearlo in Organico — il PIN è sufficiente per accedere e registrare.
            </FAQ>
            <FAQ q="Posso importare oggetti e organico da un foglio Excel?">
              L'import massivo è disponibile su richiesta dal nostro supporto. Per il setup iniziale è comunque consigliato partire dai 3 oggetti rappresentativi e crescere progressivamente, così da prendere confidenza con la struttura ambito → tipo → scadenza.
            </FAQ>
          </div>

          {/* Sezione 6 — E adesso? */}
          <H id="s6">E adesso?</H>
          <P>Hai un sistema HACCP funzionante. Da qui puoi:</P>
          <ul style={{ paddingLeft: 22, color: LT.n700, fontSize: 14.5, lineHeight: 1.85, margin: '8px 0 24px' }}>
            <li>Aggiungere il resto degli oggetti reali della tua struttura.</li>
            <li>Caricare in <Strong>Documenti</Strong> il manuale HACCP e le procedure interne.</li>
            <li>Configurare i tuoi <Strong>ambiti</Strong> e <Strong>tipi di attività</Strong> per i monitoraggi specifici della filiera.</li>
            <li>Se hai il piano TRACE, aprire il modulo <Strong>Tracciabilità</Strong> per fornitori, lotti e ricette.</li>
          </ul>

          {/* Footer feedback (semplificato, manda email) */}
          <div style={{
            marginTop: 48, padding: '20px 22px',
            background: LT.n50, borderRadius: LT.r.md, border: `1px solid ${LT.n150}`,
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: LT.n900 }}>
                Questa guida ti è stata utile?
              </div>
              <div style={{ fontSize: 12.5, color: LT.n500, marginTop: 3 }}>
                Il tuo feedback ci aiuta a migliorare la documentazione.
              </div>
            </div>
            <a
              href="mailto:info@labthree.it?subject=Feedback%20guida%20Primi%20passi&body=La%20guida%20mi%20%C3%A8%20stata%20utile.%0A%0A"
              style={{
                padding: '10px 16px', background: '#fff',
                border: `1px solid ${LT.n200}`, borderRadius: LT.r.sm,
                fontSize: 13, color: LT.n800, fontWeight: 600,
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Ico d={I.check} size={14} stroke={LT.verde} />
              Sì, utile
            </a>
            <a
              href="mailto:info@labthree.it?subject=Suggerimento%20guida%20Primi%20passi&body=Un%20punto%20da%20migliorare%3A%0A%0A"
              style={{
                padding: '10px 16px', background: 'transparent',
                border: 'none', fontSize: 13, color: LT.n600, fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              Si può migliorare
            </a>
          </div>
        </article>
      </div>

      {/* CSS responsive: TOC sopra al contenuto su schermi <960px */}
      <style>{`
        @media (max-width: 960px) {
          .primi-passi-toc { display: none !important; }
        }
      `}</style>
    </div>
  );
}
